/**
 * Maps human-readable keys to translation IDs for the /tools/smaf5-calculator
 * SEO landing page. New content (not from Framer XLIFF), so keys carry the
 * `smaf5_` prefix directly.
 *
 * The page is gated to CH alone (`page:tools/smaf5-calculator`), in all three
 * Swiss locales, so every mirror locale exempts this namespace in
 * `SKIP_NAMESPACES_PER_LOCALE` (scripts/lint-mirror-locales.ts), `de-DE`
 * included. Like `fib4Calculator` and unlike `score2Calculator`, that
 * exemption is a rollout state rather than a clinical one: sMAF-5's
 * coefficients were fitted once and are applied unchanged in every country, so
 * opening the page to Germany needs the German copy and nothing else. See the
 * gate's own description in lib/country-gates.ts.
 *
 * No key in this namespace takes an ICU placeholder. That is deliberate: the
 * band and sex definitions in `lib/smaf5.ts` store these keys as `as const`
 * literals, and a single placeholder-bearing key in the namespace would make
 * next-intl's typed `t()` demand a `values` argument at every
 * `t(band.labelKey)` call site (the TS2554 trap documented in the BMI
 * calculator's `bmi.ts`).
 */
export const smaf5CalculatorKeys = {
  // Metadata
  metaTitle: "smaf5_metaTitle",
  metaDescription: "smaf5_metaDescription",

  // Hero
  heading: "smaf5_heading", // "sMAF-5 calculator"
  subheading: "smaf5_subheading",

  // Calculator widget
  calcTitle: "smaf5_calcTitle",
  calcSexLabel: "smaf5_calcSexLabel",
  calcSexFemale: "smaf5_calcSexFemale",
  calcSexMale: "smaf5_calcSexMale",
  calcHeightLabel: "smaf5_calcHeightLabel",
  calcWeightLabel: "smaf5_calcWeightLabel",
  calcBodyHint: "smaf5_calcBodyHint",
  calcDiabetesLabel: "smaf5_calcDiabetesLabel",
  calcDiabetesYes: "smaf5_calcDiabetesYes",
  calcDiabetesNo: "smaf5_calcDiabetesNo",
  calcDiabetesHint: "smaf5_calcDiabetesHint",
  calcAstLabel: "smaf5_calcAstLabel",
  calcPlateletsLabel: "smaf5_calcPlateletsLabel",
  calcBloodHint: "smaf5_calcBloodHint",
  calcPlateletsHint: "smaf5_calcPlateletsHint",
  calcUnitCm: "smaf5_calcUnitCm", // "cm"
  calcUnitKg: "smaf5_calcUnitKg", // "kg"
  calcUnitUl: "smaf5_calcUnitUl", // "U/L"
  calcUnitGl: "smaf5_calcUnitGl", // "G/L"
  calcSubmit: "smaf5_calcSubmit",
  calcReset: "smaf5_calcReset",
  calcResultLabel: "smaf5_calcResultLabel", // "Your sMAF-5 score"
  calcBmiLabel: "smaf5_calcBmiLabel", // "BMI used"
  calcEmptyState: "smaf5_calcEmptyState",
  calcOutOfRange: "smaf5_calcOutOfRange",
  calcBodyOutOfRange: "smaf5_calcBodyOutOfRange",
  calcPlateletsUnit: "smaf5_calcPlateletsUnit",
  calcThresholdsLabel: "smaf5_calcThresholdsLabel",
  calcScaleCaption: "smaf5_calcScaleCaption",
  calcBmiFloorNote: "smaf5_calcBmiFloorNote",
  calcEligibilityNote: "smaf5_calcEligibilityNote",
  calcDisclaimer: "smaf5_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "smaf5_quizHeading",
  quizBody: "smaf5_quizBody",
  quizCta: "smaf5_quizCta",

  // Result bands (shared by the result card and the reference table)
  bandLow: "smaf5_bandLow",
  bandIntermediate: "smaf5_bandIntermediate",
  bandHigh: "smaf5_bandHigh",

  meaningLow: "smaf5_meaningLow",
  meaningIntermediate: "smaf5_meaningIntermediate",
  meaningHigh: "smaf5_meaningHigh",

  // Cut-off cells, one per band
  thrLow: "smaf5_thrLow",
  thrIntermediate: "smaf5_thrIntermediate",
  thrHigh: "smaf5_thrHigh",

  // Observed liver stiffness of 8 kPa or more, per band
  stiffnessLow: "smaf5_stiffnessLow",
  stiffnessIntermediate: "smaf5_stiffnessIntermediate",
  stiffnessHigh: "smaf5_stiffnessHigh",

  // Observed liver-related events, per band
  eventsLow: "smaf5_eventsLow",
  eventsIntermediate: "smaf5_eventsIntermediate",
  eventsHigh: "smaf5_eventsHigh",

  // Band reference table
  tableHeading: "smaf5_tableHeading",
  tableIntro: "smaf5_tableIntro",
  tableColBand: "smaf5_tableColBand",
  tableColScore: "smaf5_tableColScore",
  tableColStiffness: "smaf5_tableColStiffness",
  tableColEvents: "smaf5_tableColEvents",
  tableNote: "smaf5_tableNote",

  // What sMAF-5 is
  whatLabel: "smaf5_whatLabel",
  whatHeading: "smaf5_whatHeading",
  whatP1: "smaf5_whatP1",
  whatP2: "smaf5_whatP2",
  whatP3: "smaf5_whatP3",

  // What goes into it
  inputsLabel: "smaf5_inputsLabel",
  inputsHeading: "smaf5_inputsHeading",
  inputsIntro: "smaf5_inputsIntro",
  input1Title: "smaf5_input1Title",
  input1Body: "smaf5_input1Body",
  input2Title: "smaf5_input2Title",
  input2Body: "smaf5_input2Body",
  input3Title: "smaf5_input3Title",
  input3Body: "smaf5_input3Body",
  input4Title: "smaf5_input4Title",
  input4Body: "smaf5_input4Body",
  input5Title: "smaf5_input5Title",
  input5Body: "smaf5_input5Body",

  // Who it is for, who it is not for
  whoHeading: "smaf5_whoHeading",
  whoIntro: "smaf5_whoIntro",
  whoForTitle: "smaf5_whoForTitle",
  whoFor1: "smaf5_whoFor1",
  whoFor2: "smaf5_whoFor2",
  whoFor3: "smaf5_whoFor3",
  whoNotForTitle: "smaf5_whoNotForTitle",
  whoNotFor1: "smaf5_whoNotFor1",
  whoNotFor2: "smaf5_whoNotFor2",
  whoNotFor3: "smaf5_whoNotFor3",
  whoNotFor4: "smaf5_whoNotFor4",
  whoNote: "smaf5_whoNote",

  // Units, the half of this page that is market-specific
  unitsHeading: "smaf5_unitsHeading",
  unitsP1: "smaf5_unitsP1",
  unitsP2: "smaf5_unitsP2",
  unitsTipTitle: "smaf5_unitsTipTitle",
  unitsTipBody: "smaf5_unitsTipBody",

  // Limitations
  limitsLabel: "smaf5_limitsLabel",
  limitsHeading: "smaf5_limitsHeading",
  limitsIntro: "smaf5_limitsIntro",
  limit1Title: "smaf5_limit1Title",
  limit1Body: "smaf5_limit1Body",
  limit2Title: "smaf5_limit2Title",
  limit2Body: "smaf5_limit2Body",
  limit3Title: "smaf5_limit3Title",
  limit3Body: "smaf5_limit3Body",
  limit4Title: "smaf5_limit4Title",
  limit4Body: "smaf5_limit4Body",

  // Membership section
  memLabel: "smaf5_memLabel",
  memHeading: "smaf5_memHeading",
  memBody: "smaf5_memBody",
  memFeature1: "smaf5_memFeature1",
  memFeature2: "smaf5_memFeature2",
  memFeature3: "smaf5_memFeature3",
  memFeature4: "smaf5_memFeature4",
  memFeature5: "smaf5_memFeature5",
  memCtaPrimary: "smaf5_memCtaPrimary",
  memCtaSecondary: "smaf5_memCtaSecondary",
  memFootnote: "smaf5_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "smaf5_faqHeading",
  faqQ1: "smaf5_faqQ1",
  faqA1: "smaf5_faqA1",
  faqQ2: "smaf5_faqQ2",
  faqA2: "smaf5_faqA2",
  faqQ3: "smaf5_faqQ3",
  faqA3: "smaf5_faqA3",
  faqQ4: "smaf5_faqQ4",
  faqA4: "smaf5_faqA4",
  faqQ5: "smaf5_faqQ5",
  faqA5: "smaf5_faqA5",
  faqQ6: "smaf5_faqQ6",
  faqA6: "smaf5_faqA6",

  // Closing CTA
  ctaHeading: "smaf5_ctaHeading",
  ctaBody: "smaf5_ctaBody",
  ctaPrimary: "smaf5_ctaPrimary",
  ctaSecondary: "smaf5_ctaSecondary",

  // Legal / medical
  sourceLabel: "smaf5_sourceLabel",
  sourceBody: "smaf5_sourceBody",
  disclaimer: "smaf5_disclaimer",
} as const
